import ReactECharts from 'echarts-for-react';

/**
 * KernelVersionChart_4 — CONTENT alternative to KernelVersionChart (blog05).
 * GPT-4 vs GPT-3.5 drift DIRECTION over the same three months (March → June
 * 2023). The finding: drift is unpredictable even in direction — GPT-3.5's
 * prime-vs-composite accuracy went UP (49.6% → 76.2%) while GPT-4's went DOWN
 * (84.0% → 51.1%); code generation went down for both.
 *
 * Same source as KernelVersionChart: Chen, Zaharia & Zou, "How Is ChatGPT's
 * Behavior Changing over Time?", Harvard Data Science Review 6(2), 2024 —
 * arXiv:2307.09009v3, https://arxiv.org/abs/2307.09009
 * Data mirrors public/blog/gpt4-drift-chen-2023.csv (both model blocks).
 *
 * Chart-type-first: paired values for two models across tasks → ECharts native
 * grouped bar (four series). Brand colors; opaque white background.
 */

const BRAND = {
  teal: '#0AB3BF',
  info: '#3B82F6',
  slate: '#334155',
  slateBar: '#94A3B8', // GPT-4 March
  slateBarLight: '#CBD5E1', // GPT-3.5 March
  muted: '#64748B',
  surface: '#FFFFFF',
};

const TASKS = [
  { label: 'Prime vs composite', metric: 'accuracy', n: 1000, gpt4: [84.0, 51.1], gpt35: [49.6, 76.2] },
  { label: 'Happy-number counting', metric: 'accuracy', n: 500, gpt4: [83.6, 35.2], gpt35: [30.6, 48.2] },
  { label: 'Code generation', metric: 'directly executable', n: 50, gpt4: [52.0, 10.0], gpt35: [22.0, 2.0] },
  { label: 'Sensitive questions', metric: 'response rate', n: 100, gpt4: [21.0, 5.0], gpt35: [2.0, 8.0] },
];

/** ONE shared percent formatter — tooltip, axis ticks, and bar labels all use it. */
const formatPercent = (v: number) => `${v}%`;

const SERIES = [
  { name: 'GPT-4 · March', pick: (t: (typeof TASKS)[number]) => t.gpt4[0], color: BRAND.slateBar },
  { name: 'GPT-4 · June', pick: (t: (typeof TASKS)[number]) => t.gpt4[1], color: BRAND.teal },
  { name: 'GPT-3.5 · March', pick: (t: (typeof TASKS)[number]) => t.gpt35[0], color: BRAND.slateBarLight },
  { name: 'GPT-3.5 · June', pick: (t: (typeof TASKS)[number]) => t.gpt35[1], color: BRAND.info },
];

export const KernelVersionChart_4 = ({ height = 320 }: { height?: number }) => {
  const option = {
    backgroundColor: BRAND.surface,
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      backgroundColor: '#1E293B',
      borderColor: BRAND.slate,
      textStyle: { color: '#F8FAFC', fontSize: 13 },
      formatter: (params: { seriesName: string; value: number; dataIndex: number; marker?: string }[]) => {
        const t = TASKS[params[0].dataIndex];
        const rows = params.map((p) => `${p.marker} ${p.seriesName}: <b>${formatPercent(p.value)}</b>`).join('<br/>');
        return `${t.label} — ${t.metric} (n=${t.n}), March → June 2023<br/>${rows}`;
      },
    },
    legend: {
      top: 8,
      textStyle: { color: BRAND.slate, fontSize: 12 },
      data: SERIES.map((s) => s.name),
    },
    grid: { left: 48, right: 24, top: 56, bottom: 60 },
    xAxis: {
      type: 'category',
      data: TASKS.map((t) => t.label),
      axisLine: { lineStyle: { color: BRAND.slate } },
      axisLabel: { color: BRAND.muted, fontSize: 12, fontWeight: 600, interval: 0, width: 120, overflow: 'break' },
      axisTick: { show: false },
    },
    yAxis: {
      type: 'value',
      max: 100,
      name: 'March → June 2023 (%)',
      nameTextStyle: { color: BRAND.muted, fontSize: 11 },
      axisLabel: { color: BRAND.muted, fontSize: 12, formatter: (v: number) => formatPercent(v) },
      splitLine: { lineStyle: { color: BRAND.slate, opacity: 0.35 } },
    },
    series: SERIES.map((s) => ({
      name: s.name,
      type: 'bar',
      barGap: '10%',
      itemStyle: { color: s.color, borderRadius: [2, 2, 0, 0] },
      data: TASKS.map(s.pick),
      label: {
        // Label only the June (endpoint) bars — March values stay readable via the axis.
        show: s.name.endsWith('June'),
        position: 'top',
        color: BRAND.slate,
        fontSize: 10,
        fontWeight: 600,
        formatter: (p: { value: number }) => formatPercent(p.value),
      },
    })),
  };

  return (
    <div className="my-8 rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700 bg-white p-2">
      <ReactECharts option={option} style={{ height }} opts={{ renderer: 'canvas' }} />
      <p className="mt-2 text-xs text-slate-500 dark:text-slate-400 text-center">
        Figure 1. You can&apos;t even bet on the direction: in the same three months GPT-3.5 got better at math
        (prime-vs-composite 49.6% → 76.2%) and worse at code — while GPT-4 moved the opposite way. (Same source: Chen,
        Zaharia &amp; Zou 2023, arXiv:2307.09009v3)
      </p>
      <p className="mt-1 text-label-xs text-slate-600 dark:text-slate-500 text-center">
        Labels show the June endpoint of each pair; &ldquo;sensitive questions&rdquo; is a response rate, not an
        accuracy.
      </p>
    </div>
  );
};
